
import { useCallback, useEffect, useState } from "react";
import { Modal, Select, Input, Flex, Switch } from "antd";
import { AgentScript } from "../../atoms/agentScript/AgentScript";
import { Button } from "../../atoms/button/Button";
import { Container } from "../../atoms/container/Container";
import { Text } from "../../atoms/text/Text";
import { AmbulanceCard } from "../../molecules/ambulanceCard/AmbulanceCard";
import style from "./ambulanceBooking.module.scss";
import { CustomSteps } from "../../molecules/ambulanceSteps/CustomSteps";
import { useAmbulanceBooking } from "../../../hooks/useAmbulanceBooking/UseAmbulanceBooking";
import { usePaymentStatus } from "../../../hooks/usePaymentStatus/usePaymentStatus";
import { PaymentButton } from "../paymentButton/PaymentButton";
import { AmbulanceDistance } from "../../molecules/ambulanceDistance/ambulanceDistance";
import { SelectAssistance } from "../selectAssistance/SelectAssistance";
import { AmbulancePickupTime } from "../../molecules/ambulancePickupTime/AmbulancePickupTime";
import { AmbulanceType } from "../../molecules/ambulanceType/AmbulanceType";
import { AmbulanceRateCard } from "../../molecules/ambulanceRateCard/AmbulanceRateCard";
import { AmbulanceAvailable } from "../../molecules/ambulanceAvailable/AmbulanceAvailable";
import { fareCalculationApi } from "../../../http";
import { UseGetStoreData } from "../../../hooks/getStoreData/UseGetStoreData";
import { ticketUpdateStatusSuccess } from "../../../redux/ticketUpdateSlice/ticketUpdateSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

interface AmbulanceBookingProps {
  pickupLocation: any;
  pickupCity: string;
  dropLocation: any;
  label: string;
}

interface calculateFarePayloadDTO {
  ambulanceType: string;
  distance: number;
  rateCardName?: string;
  wheelchair?: boolean;
  stretcher?: boolean;
  paramedic?: boolean;
  helper?: number;
  doctorOnBoard?: boolean;
}
interface FareData {
  baseFare: number;
  totalFare: number;
  extraCharges: number;
  rateDetails?: {
    extraChargeDetails?: {
      stretcher?: { quantity: boolean; unitPrice: number; total: number };
      paramedic?: { quantity: number; unitPrice: number; total: number };
      helper?: { quantity: number; unitPrice: number; total: number };
      wheelchair?: { quantity: boolean; unitPrice: number; total: number };
      doctorOnBoard?: { quantity: boolean; unitPrice: number; total: number };
    };
  };
}


export const AmbulanceBooking: React.FC<AmbulanceBookingProps> = ({
  pickupLocation,
  pickupCity,
  dropLocation,
  label
}) => {
  const {
    currentStep,
    handleSendAmbulanceReq,
    handleStepChanges,
    getTicketStatus,
    sendAMbulancePaymentLink,
    ambulanceDistanceBetween,
    handleEdit,
    ambulancePickupTime,
    setAmbulancePickupTime,
    isCityCovered,
    pocorateCard,
    handleAmbulanceType,
    selectedAmbulanceType,
    ambulanceData,
    rateCards,
    selectedRateCard,
    handleRateCardChange,
    rateCardSource,
    copyMessage,
    copySubHeader,
    copyFields,
    isCopyModalOpen,
    closeCopyModal,
    handleRejectAmbulance,
    isRejected,
    isDispatched,
    ambulanceMessageSend,
  } = useAmbulanceBooking();
  const { paymentBtnStatus_Ambulance, sendPaymentLink, refreshPaymentStatus, isRefreshing } = usePaymentStatus();
  const { getTicketUpdateData } = UseGetStoreData();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fareData, setFareData] = useState<FareData | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const [isChargeable, setIsChargeable] = useState<boolean>(false);
  // 🔹 NEW: Fare Calculation API
  // Replace the calculateFare function with this version:
  //  // Add correct import path

  // Then replace the calculateFare function:

  // Replace both useEffect blocks with this single one:
  const calculateFare = useCallback(async () => {
    if (!ambulanceDistanceBetween || !selectedAmbulanceType || !selectedRateCard) {
      return;
    }

    const distanceMatch = ambulanceDistanceBetween.match(/[\d.]+/);
    const distance = distanceMatch ? parseFloat(distanceMatch[0]) : 0;

    const payload: calculateFarePayloadDTO = {
      ambulanceType: selectedAmbulanceType,
      distance,
      rateCardName: selectedRateCard,
    };

    const assistance = getTicketUpdateData?.typeOfAssistanceRequired;
    if (assistance) {
      if (assistance.wheelChair) payload.wheelchair = true;
      if (assistance.stretcher) payload.stretcher = true;
      if (assistance.paramedic) payload.paramedic = true;
      if (assistance.additionalHelp && assistance.numberOfHelpers) {
        payload.helper = assistance.numberOfHelpers;
      }
      if (assistance.DoctorOnBoard) payload.doctorOnBoard = true;
    }
      const result = await fareCalculationApi(payload);
      const fare = result.data?.data ?? null;
      setFareData(fare);
      dispatch(ticketUpdateStatusSuccess({
        estimatedFare: fare,
        selectedAmbulanceType: selectedAmbulanceType
      }));
  }, [ambulanceDistanceBetween, selectedAmbulanceType, selectedRateCard, getTicketUpdateData?.typeOfAssistanceRequired, dispatch]);

  useEffect(() => {
    calculateFare();
  }, [calculateFare]);

  const REJECTION_REASONS = [
    "Too Expensive",
    "Doctor advised not required",
    "Arranged own",
    "Expected shorter ETA",
    "Just inquiry",
    "Other",
  ];

  const handleRejectConfirm = async () => {
    const reason = rejectReason === "Other" ? otherReason.trim() : rejectReason;
    if (!reason) return;
    await handleRejectAmbulance(reason, isChargeable);
    setIsRejectModalOpen(false);
    setRejectReason("");
    setOtherReason("");
    setIsChargeable(false);
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "32px" }}>
            <span>🚑 Ambulance Message</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(copyMessage);
                closeCopyModal();
              }}
              style={{
                padding: "4px 14px",
                background: "#00A7B5",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              📋 Copy & Close
            </button>
          </div>
        }
        open={isCopyModalOpen}
        onCancel={closeCopyModal}
        width={720}
        footer={
          <button
            onClick={() => {
              navigator.clipboard.writeText(copyMessage);
              closeCopyModal();
            }}
            style={{
              width: "100%",
              padding: "10px",
              background: "#00A7B5",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            📋 Copy & Close
          </button>
        }
      >
        <div style={{ fontWeight: 700, fontSize: "15px", color: "#00A7B5", marginBottom: "6px" }}>
          Ambulance Request from Pococare
        </div>
        <div style={{ fontSize: "13px", color: "#444", marginBottom: "12px", lineHeight: "1.5" }}>
          {copySubHeader}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px", padding: "4px 0", cursor: "default" }}>
          {copyFields.map(({ label, value }) => (
            <div
              key={label}
              style={{
                display: "grid",
                gridTemplateColumns: "130px 1fr",
                gap: "8px",
                padding: "6px 10px",
                background: "#f9f9f9",
                borderRadius: "6px",
                borderLeft: "3px solid #00A7B5",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {label}
              </div>
              <div style={{ fontSize: "13px", color: "#222", wordBreak: "break-word", lineHeight: "1.4" }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </Modal>
      <Container className={style.wrapper}>
      <Container className={style.spacing}>
          <Flex className={style.labelRow}>
            <Text text="Ambulance Booking" className={style.title} />
            <AgentScript
              text='"I will arrange an ambulance. I just need to understand a few requirements."'
              className={style.mobileOnly}
            />
          </Flex>
          <AgentScript
            text='"I will arrange an ambulance. I just need to understand a few requirements."'
            className={style.desktopOnly}
          />
          <Text text="Selected Pickup Point" className={style.sub_title} />
          <AmbulanceCard
            label={label || "N/F"}
            address={pickupLocation}
            location="Pick-up Location:"
            onEdit={() => handleEdit("Pick-up Location")}
          />

          <Text text="Selected Drop Point" className={style.sub_title} />
          <AmbulanceCard
            label={dropLocation?.hospitalName || "N/F"}
            address={dropLocation?.address || "N/F"}
            location="Drop Location:"
            onEdit={() => handleEdit("Drop Location")}
          />

          <AmbulanceDistance
            label="Distance Between:"
            distance={ambulanceDistanceBetween}
          />

          <AmbulancePickupTime
            ambulancePickupTime={ambulancePickupTime}
            handleAmbulancePickupTime={setAmbulancePickupTime}
            disable={isDispatched}
          />

          <AmbulanceRateCard
            rateCards={rateCards}
            handleRateCardChange={handleRateCardChange}
            selectedRateCard={selectedRateCard}
            disable={paymentBtnStatus_Ambulance.isPaid}
            rateCardSource={rateCardSource}
          />

          <AmbulanceType
            pocoRateCard={pocorateCard}
            handleAmbulanceType={handleAmbulanceType}
            selectedAmbulanceType={selectedAmbulanceType}
            disable={getTicketStatus === "completed"}
          />

          <AmbulanceAvailable
            isAmbulanceAvailable={`${isCityCovered(pickupCity)}`}
          />

          <SelectAssistance ambulanceData={ambulanceData} isDispatched={isDispatched} />
          <AgentScript text={`"We are dispatching an ambulance to ${pickupLocation || 'N/A'} and it will take the patient to ${dropLocation?.hospitalName || 'N/A'}."`} />

          {/* 🔹 UPDATED: Estimated Fare with Info Icon */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Text
              text={`Estimated Fare : Rs. ${fareData?.totalFare ?? "--"}`}
              className={style.title}
            />
            {fareData && (
              <div
                className="fare-info-wrapper"
                style={{ position: "relative", display: "inline-block" }}
              >
                <span
                  style={{
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#1890ff",
                  }}
                >
                  ℹ️
                </span>
                <div
                  className="fare-tooltip"
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "white",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    padding: "12px",
                    minWidth: "220px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    opacity: 0,
                    visibility: "hidden",
                    transition: "opacity 0.2s, visibility 0.2s",
                    zIndex: 1000,
                    whiteSpace: "nowrap",
                  }}
                >
                  <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>
                    Fare Breakdown
                  </div>
                  <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                    <div>Base Fare: Rs. {fareData.baseFare}</div>
                    {fareData.extraCharges > 0 && (
                      <>
                        <div>Extra Charges: Rs. {fareData.extraCharges}</div>
                        {fareData.rateDetails?.extraChargeDetails && (
                          <div style={{ marginLeft: "12px", fontSize: "12px", color: "#666" }}>
                            {(fareData.rateDetails.extraChargeDetails.stretcher?.total ?? 0) > 0 && (
                              <div>• Stretcher: Rs. {fareData.rateDetails.extraChargeDetails.stretcher?.total}</div>
                            )}
                            {(fareData.rateDetails.extraChargeDetails.paramedic?.total ?? 0) > 0 && (
                              <div>• Paramedic: Rs. {fareData.rateDetails.extraChargeDetails.paramedic?.total}</div>
                            )}
                            {(fareData.rateDetails.extraChargeDetails.helper?.total ?? 0) > 0 && (
                              <div>• Helper: Rs. {fareData.rateDetails.extraChargeDetails.helper?.total}</div>
                            )}
                            {(fareData.rateDetails.extraChargeDetails.wheelchair?.total ?? 0) > 0 && (
                              <div>• Wheelchair: Rs. {fareData.rateDetails.extraChargeDetails.wheelchair?.total}</div>
                            )}
                            {(fareData.rateDetails.extraChargeDetails.doctorOnBoard?.total ?? 0) > 0 && (
                              <div>• Doctor: Rs. {fareData.rateDetails.extraChargeDetails.doctorOnBoard?.total}</div>
                            )}

                          </div>
                        )}
                      </>
                    )}
                    <div style={{ borderTop: "1px solid #d9d9d9", marginTop: "8px", paddingTop: "8px", fontWeight: "600" }}>
                      Total: Rs. {fareData.totalFare}
                    </div>
                  </div>
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderTop: "6px solid white",
                  }} />
                </div>
              </div>
            )}
            {fareData && fareData.totalFare > 0 && (
              <AgentScript
                text={`"This will be a paid ambulance service and the estimated cost is ₹${fareData.totalFare}. Shall I go ahead and arrange this for you?"`}
                className={style.mobileOnly}
              />
            )}
          </div>

          <style>{`
  .fare-info-wrapper:hover .fare-tooltip {
    opacity: 1 !important;
    visibility: visible !important;
  }
`}</style>


          {fareData && fareData.totalFare > 0 && (
            <AgentScript
              text={`"This will be a paid ambulance service and the estimated cost is ₹${fareData.totalFare}. Shall I go ahead and arrange this for you?"`}
              className={style.desktopOnly}
            />
          )}

          <PaymentButton
            btnDisable={getTicketStatus === "completed"}
            component="Ambulance"
            paymentBtnStatus={paymentBtnStatus_Ambulance}
            style={style.link_btn}
            createPaymentLink={sendAMbulancePaymentLink}
            sendPaymentLink={() => sendPaymentLink("Ambulance")}
            onRefreshStatus={refreshPaymentStatus}
            isRefreshing={isRefreshing}
          />
          <AgentScript text='"Thank you. I will proceed with the dispatch."' />
        </Container>

      <Flex justify="space-evenly" align="center">
          <Button
            disable={getTicketStatus === "completed"}
            className={
              getTicketStatus === "completed" ? style.is_disabled : style.button
            }
            text="Ambulance Message"
            onClick={handleSendAmbulanceReq}
          />
        <Button
          disable={false}
          className={style.providers_btn}
          text="Ambulance Providers"
          onClick={() => navigate('/ambulance-providers')}
        />
      </Flex>

        <Modal
          title="Reason for Rejection"
          open={isRejectModalOpen}
          onCancel={() => { setIsRejectModalOpen(false); setRejectReason(""); setOtherReason(""); setIsChargeable(false); }}
          onOk={handleRejectConfirm}
          okText="Submit"
          okButtonProps={{ disabled: !rejectReason || (rejectReason === "Other" && !otherReason.trim()) }}
        >
          <Flex vertical gap={12} style={{ marginTop: 12 }}>
            <Select
              placeholder="Select a reason"
              value={rejectReason || undefined}
              onChange={(val) => { setRejectReason(val); setOtherReason(""); }}
              options={REJECTION_REASONS.map((r) => ({ label: r, value: r }))}
              style={{ width: "100%" }}
            />
            {rejectReason === "Other" && (
              <Input.TextArea
                placeholder="Please specify the reason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                rows={3}
              />
            )}
            <Flex align="center" gap={8}>
              <span>Is cancellation chargeable</span>
              <Switch checked={isChargeable} onChange={setIsChargeable} />
            </Flex>
          </Flex>
        </Modal>

        <Flex className={style.labelRow}>
          <Text text="Ambulance Tracking" className={style.title} />
          <AgentScript
            text='"You and your emergency contacts will receive WhatsApp updates with: Ambulance details, ETA, Real time tracking updates"'
            className={style.mobileOnly}
          />
        </Flex>
        <AgentScript
          text='"You and your emergency contacts will receive WhatsApp updates with: Ambulance details, ETA, Real time tracking updates"'
          className={style.desktopOnly}
        />
        <div style={{ width: "100%" }}>
          <CustomSteps
            currentStep={currentStep}
            setCurrentStep={handleStepChanges}
            totalSteps={4}
          />
        </div>

        <Flex justify="space-between" align="center">
          <Button
            disable={!ambulanceData || ambulanceData.length === 0 || getTicketStatus === "completed"|| !ambulanceData?.[0]?.ambulanceProvider}
            className={!ambulanceData || ambulanceData.length === 0 || getTicketStatus === "completed" || !ambulanceData?.[0]?.ambulanceProvider ? style.is_disabled : style.extra_charge_btn}
            text="Extra Charge"
            onClick={() => navigate('/extra-charges')}
          />
          {currentStep[3] !== 1 && ambulanceMessageSend && (
            <Button
              disable={getTicketStatus === "completed" || isRejected}
              className={style.reject_btn}
              text="Rejection Reason"
              onClick={() => setIsRejectModalOpen(true)}
            />
          )}
        </Flex>
      </Container>
    </>
  );
};
